import { useEffect, useState } from "react";
import { Loader2, Mail, Phone, Home as HomeIcon, Calendar, Clock, PhoneCall, Paperclip } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function Admin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/bookings`)
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="min-h-screen bg-cream py-16 px-6 md:px-12" data-testid="admin-page">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-end justify-between mb-10 border-b border-border pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-blush mb-2">(Internal)</p>
            <h1 className="font-serif font-light text-espresso text-4xl md:text-5xl">Booking Requests</h1>
          </div>
          <span data-testid="admin-count" className="font-serif text-3xl text-blush">{rows.length}</span>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-cocoa py-20 justify-center"><Loader2 className="animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground font-light py-20 text-center">No booking requests yet.</p>
        ) : (
          <div className="space-y-4" data-testid="admin-list">
            {rows.map((r) => (
              <div key={r.id} className="bg-card border border-border p-6 md:p-8">
                <div className="grid md:grid-cols-[1fr_auto] gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <h3 className="font-serif text-2xl text-espresso">{r.name}</h3>
                      {r.service && <span className="text-xs uppercase tracking-[0.18em] bg-blush-light text-cocoa px-3 py-1">{r.service}</span>}
                      {r.callout && <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] bg-espresso text-cream px-3 py-1"><PhoneCall size={12} /> Call-out</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-cocoa font-light">
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-2 hover:text-blush"><Mail size={14} /> {r.email}</a>
                      {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-2 hover:text-blush"><Phone size={14} /> {r.phone}</a>}
                      {r.preferred_date && <span className="inline-flex items-center gap-2"><Calendar size={14} /> {r.preferred_date}</span>}
                      {r.preferred_time && <span className="inline-flex items-center gap-2"><Clock size={14} /> {r.preferred_time}</span>}
                      {r.property_details && <span className="inline-flex items-center gap-2"><HomeIcon size={14} /> {r.property_details}</span>}
                    </div>
                    {r.message && <p className="mt-4 text-muted-foreground font-light max-w-2xl">{r.message}</p>}
                  </div>
                  <div className="text-left md:text-right text-xs text-cocoa/70 font-light whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>

                {Array.isArray(r.attachments) && r.attachments.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-border">
                    <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-blush mb-3"><Paperclip size={13} /> {r.attachments.length} attachment{r.attachments.length > 1 ? "s" : ""}</p>
                    <div className="flex flex-wrap gap-3">
                      {r.attachments.map((a, i) => {
                        const url = `${API}/files/${a.path}`;
                        const isVideo = (a.content_type || "").startsWith("video");
                        return isVideo ? (
                          <a key={i} href={url} target="_blank" rel="noreferrer" className="w-28 h-28 bg-espresso text-cream flex items-center justify-center text-xs uppercase tracking-wide hover:bg-blush transition-colors">Video ▶</a>
                        ) : (
                          <a key={i} href={url} target="_blank" rel="noreferrer" className="w-28 h-28 overflow-hidden bg-muted block">
                            <img src={url} alt={a.filename || "attachment"} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
